import { ChangeEvent, useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  IconButton,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { SearchNormal1 } from 'iconsax-react';

import MainCard from 'components/MainCard';
import SimpleBar from 'components/third-party/SimpleBar';
// import ClientFilter from 'components/ClientFilter';
import useApi from 'hooks/useApi';
// import useSelection from 'hooks/useSelection';
import { ThemeMode } from 'types/config';
import { Announcement, AnnouncementKind, ANNOUNCEMENT_KIND_LABELS } from 'types/auth';

import useAnnouncements from 'contexts/AnnouncementsContext';

import AnnouncementDetail from './components/AnnouncementDetail';
import AnnouncementFormDialog from './components/AnnouncementFormDialog';
import AnnouncementsList from './components/AnnouncementsList';

const INNER_DRAWER_WIDTH = 320;

// "Mark all as read" is hidden for now, by request. Left wired rather than deleted: the
// context method, the endpoint and the handler all still work, so restoring it is this
// one flag. Worth remembering what it was for before it goes back or goes away — reads
// clear one announcement at a time, and new joiners now start with their client's entire
// history unread, so without it a long back catalogue is cleared only click by click.
const SHOW_MARK_ALL_READ: boolean = false;
const PAGE_SIZE = 100;
const PAGE_GUARD = 50;

/**
 * Announcements — a read-only changelog for clients, authored by a Super Admin.
 *
 * Laid out like the Processes page — a fixed list panel on the left, the selected
 * announcement on the right — but without that page's collapse toggle, and with the
 * detail as a separate bordered sheet rather than one card split down the middle.
 * Selection is local state rather than a route param: the list response already carries
 * the full document, so opening one costs no extra request.
 *
 * Every management control is gated on `isSuperAdmin`, which the server decides. That
 * gate is convenience only; the write endpoints reject non-Super-Admins regardless.
 */
const AnnouncementsPage = () => {
  const theme = useTheme();
  const { getAnnouncements, deleteAnnouncement, showToast } = useApi();
  // const { clientOptions } = useSelection();
  const {
    isSuperAdmin,
    unreadCount,
    loading: visibilityLoading,
    markOneRead,
    markAllRead,
    refresh: refreshUnreadCount
  } = useAnnouncements();

  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [search, setSearch] = useState('');
  // 'all' is a UI-only value. It goes out as no `type` param at all — the server reads an
  // absent one as unfiltered, and drops an unrecognised one rather than returning nothing.
  const [typeFilter, setTypeFilter] = useState<AnnouncementKind | 'all'>('all');
  // const [client, setClient] = useState<string | null>(null);

  // Selection is held as a uid and resolved against the loaded page rather than stored
  // as a document. The create/update responses come back with `clients` unpopulated
  // (raw ObjectIds), so rendering them directly would blow up the audience chips — and
  // this way a selection that falls off the page (deleted, or filtered out) clears itself.
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const selected = announcements.find((item) => item.uid === selectedUid) ?? null;

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Announcement | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Announcement | null>(null);
  const [deleting, setDeleting] = useState(false);

  const drawerBG = theme.palette.mode === ThemeMode.DARK ? 'dark.main' : 'white';

  // The whole feed in one list, no pager. The endpoint is paginated and has no "all"
  // flag, so walk the pages until `hasMore` clears rather than guessing a size big
  // enough — that way nothing is silently cut off as the changelog grows. PAGE_GUARD
  // stops a misreported `hasMore` from looping forever.
  const loadAnnouncements = async () => {
    setListLoading(true);
    try {
      const collected: Announcement[] = [];
      let page = 1;

      for (; page <= PAGE_GUARD; page += 1) {
        const response = await getAnnouncements({
          page,
          size: PAGE_SIZE,
          keyword: search || undefined,
          type: typeFilter === 'all' ? undefined : typeFilter
          // Super Admin only — the server ignores it for anyone else.
          // client: client || undefined
        });

        if (!response?.success) {
          showToast(response?.message || 'Could not load announcements', 'error');
          return;
        }

        collected.push(...(response.data?.list ?? []));
        if (!response.data?.hasMore) break;
      }

      setAnnouncements(collected);
    } catch (error: any) {
      showToast(error?.message || 'Could not load announcements', 'error');
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    loadAnnouncements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, typeFilter]);

  // On open only — deliberately not alongside the list fetch above, which re-runs on every
  // keystroke in the search box and on every filter change.
  //
  // The badge otherwise comes from a single call made when the app mounted, while this
  // list is refetched every time the page opens. Anything published in between left the
  // two disagreeing, which is what puts a "New" chip on a row while the nav item beside it
  // shows no number at all. One extra count query on open keeps them telling one story.
  useEffect(() => {
    refreshUnreadCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSearch(event.target.value);
  };

  // Opening one announcement clears one from the nav badge. This is the only place a read
  // is recorded — deliberately not on page mount, which would clear the whole badge for
  // someone who arrived and read nothing.
  //
  // The `isRead` guard is bandwidth, not correctness: the endpoint is idempotent, so
  // re-clicking a row the user has already read would simply return the same count.
  const handleSelect = (announcement: Announcement) => {
    setSelectedUid(announcement.uid);

    // Patched locally rather than refetched so the "New" chip clears immediately — a
    // refetch here would rebuild the list under the user as they start reading.
    if (announcement.isRead === false) {
      setAnnouncements((prev) => prev.map((item) => (item.uid === announcement.uid ? { ...item, isRead: true } : item)));
    }

    // Fired on every open, including an already-read one — the guard that used to skip
    // those is gone on purpose. The badge has nothing left to learn from re-opening a
    // read row, but the server writes a view receipt on each call (first open, latest
    // open, count), and skipping it would leave that record blank for everything behind
    // the watermark or cleared by "Mark all read". The endpoint is idempotent, so the
    // extra calls cost one request and move no count.
    markOneRead(announcement.uid);
  };

  const handleMarkAllRead = async () => {
    await markAllRead();
    setAnnouncements((prev) => prev.map((item) => ({ ...item, isRead: true })));
  };

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = () => {
    setEditing(selected);
    setFormOpen(true);
  };

  const handleSaved = (saved: Announcement) => {
    setFormOpen(false);
    setEditing(null);
    if (saved?.uid) setSelectedUid(saved.uid);
    // Refetched rather than patched locally: the refetched copy is the one with
    // `clients` populated, which create and update do not return.
    loadAnnouncements();
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      const response = await deleteAnnouncement(pendingDelete.uid);
      if (response?.success) {
        showToast('Announcement deleted', 'success');
        setSelectedUid(null);
        setPendingDelete(null);
        loadAnnouncements();
      } else {
        showToast(response?.message || 'Could not delete the announcement', 'error');
      }
    } catch (error: any) {
      showToast(error?.message || 'Could not delete the announcement', 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', padding: '8px', position: 'relative', overflow: 'hidden' }}>
      {/* No collapse toggle here, unlike the Processes page: the list is the only way to
          reach an announcement, so there is nothing to gain from hiding it — and the
          floating button sat on top of the detail title. The drawer stays open. */}
      <Box
        sx={{
          width: INNER_DRAWER_WIDTH,
          minWidth: INNER_DRAWER_WIDTH,
          flexShrink: 0,
          overflow: 'hidden'
        }}
      >
        <Drawer
          sx={{
            width: INNER_DRAWER_WIDTH,
            flexShrink: 0,
            zIndex: 0,
            '& .MuiDrawer-paper': {
              height: 'auto',
              width: INNER_DRAWER_WIDTH,
              boxSizing: 'border-box',
              position: 'relative',
              border: 'none',
              borderRadius: '12px'
            }
          }}
          variant="persistent"
          anchor="left"
          open
        >
          <MainCard sx={{ bgcolor: drawerBG, borderRadius: '12px' }} border content={false}>
            <Box sx={{ p: 1, pb: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <OutlinedInput
                  size="small"
                  id="input-search-announcements"
                  placeholder="Search"
                  value={search}
                  onChange={handleSearch}
                  sx={{
                    // Takes whatever the row's other controls leave behind — the full
                    // width when there are none.
                    flex: 1,
                    minWidth: 0,
                    background: '#fff',
                    borderRadius: 1,
                    boxShadow: '0 1px 2px rgba(60,60,60,0.04)',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d1d5db' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                    height: 40
                  }}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton size="small" edge="end" sx={{ p: 0.5 }}>
                        <SearchNormal1 style={{ fontSize: 18, color: '#4B5563' }} />
                      </IconButton>
                    </InputAdornment>
                  }
                />

                {/* Sits inline with search rather than on a row of its own. It is sized to
                    its label instead of stretching, so search keeps the slack — at 320px
                    there is not enough width to share three controls evenly.

                    Filtering is server-side, not a filter over what is already loaded: the
                    feed is walked page by page, so a client-side filter would only ever
                    hide rows from whatever happened to be fetched. */}
                <TextField
                  select
                  size="small"
                  value={typeFilter}
                  onChange={(event) => setTypeFilter(event.target.value as AnnouncementKind | 'all')}
                  sx={{
                    flexShrink: 0,
                    width: 108,
                    bgcolor: '#fff',
                    borderRadius: 1,
                    '& .MuiOutlinedInput-root': { height: 40, borderRadius: 1 },
                    '& .MuiSelect-select': { fontSize: 13, py: 0, pl: 1.25 },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d1d5db' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' }
                  }}
                >
                  <MenuItem value="all" sx={{ fontSize: 13 }}>
                    All types
                  </MenuItem>
                  {(Object.keys(ANNOUNCEMENT_KIND_LABELS) as AnnouncementKind[]).map((kind) => (
                    <MenuItem key={kind} value={kind} sx={{ fontSize: 13 }}>
                      {ANNOUNCEMENT_KIND_LABELS[kind]}
                    </MenuItem>
                  ))}
                </TextField>

                {isSuperAdmin && (
                  <Button
                    type="button"
                    onClick={openCreate}
                    sx={{
                      color: '#fff',
                      bgcolor: 'secondary.dark',
                      '&:hover': { bgcolor: 'secondary.main' },
                      minWidth: 56,
                      flexShrink: 0,
                      height: 40,
                      fontWeight: 600,
                      fontSize: 15,
                      boxShadow: '0 1px 2px rgba(60,60,60,0.04)'
                    }}
                    variant="contained"
                  >
                    Add
                  </Button>
                )}

                {/* The escape hatch: reading clears one at a time, so someone returning to
                    twelve unread needs a way out that is not twelve clicks.

                    An icon rather than the "Mark all read" text button it used to be — the
                    filter now occupies the width that button was borrowing, and a third
                    labelled control would squeeze search down to nothing. Still hidden at
                    0, so it is never a dead control. Never shares the row with Add: Add is
                    Super Admin only, and a Super Admin has no unread count to clear. */}
                {SHOW_MARK_ALL_READ && !isSuperAdmin && unreadCount > 0 && (
                  <Tooltip title="Mark all as read">
                    <IconButton
                      type="button"
                      color="primary"
                      onClick={handleMarkAllRead}
                      sx={{
                        flexShrink: 0,
                        width: 40,
                        height: 40,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: '#d1d5db',
                        bgcolor: '#fff',
                        '&:hover': { borderColor: 'primary.main', bgcolor: '#fff' }
                      }}
                    >
                      <DoneAllIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Box>

            {/* Taller than the Processes drawer: there is no pager row to leave space for.
                Back to the original 165px now that the type filter shares the search row
                instead of taking one of its own — SimpleBar needs a fixed height to scroll
                rather than push the drawer past the bottom of the viewport. */}
            <SimpleBar sx={{ overflowX: 'hidden', height: 'calc(100vh - 165px)', minHeight: 'calc(100vh - 165px)' }}>
              <Box
                sx={{
                  // Was p: 3. The rows are bordered cards now and they need to read as the
                  // full width of the panel; a 24px gutter around them made the list look
                  // inset from a drawer that is only 320px wide to begin with. 8px on the
                  // sides is enough to keep the borders off the panel edge, and matches the
                  // padding on the search row above.
                  px: 1,
                  pt: 0,
                  pb: 1,
                  scrollbarColor: '#bdbdbd #f0f0f0',
                  '::-webkit-scrollbar': { width: '8px', backgroundColor: '#f0f0f0' },
                  '::-webkit-scrollbar-thumb': { backgroundColor: '#bdbdbd', borderRadius: '4px' }
                }}
              >
                <AnnouncementsList announcements={announcements} selectedUid={selectedUid} loading={listLoading} onSelect={handleSelect} />
              </Box>
            </SimpleBar>
          </MainCard>
        </Drawer>
      </Box>

      {/* The detail reads as its own sheet — margin off the list, a full border and
          rounded corners, on paper rather than the grey the drawer sits on. */}
      <MainCard
        content={false}
        border
        sx={{
          bgcolor: theme.palette.mode === ThemeMode.DARK ? 'dark.main' : 'background.paper',
          borderRadius: '12px',
          borderColor: 'divider',
          width: '100%',
          ml: 2,
          mr: 0.5
        }}
      >
        <SimpleBar sx={{ height: 'calc(100vh - 140px)', overflowX: 'hidden' }}>
          {/* Selection wins over the loading state: refetches happen on every keystroke
              in the search box, and the open announcement should not flicker away. */}
          {selected ? (
            <AnnouncementDetail
              announcement={selected}
              canManage={isSuperAdmin}
              onEdit={openEdit}
              onDelete={() => setPendingDelete(selected)}
            />
          ) : listLoading || visibilityLoading ? (
            <Box sx={{ width: '100%', height: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ width: '100%', height: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Box>
                <Typography variant="h3" textAlign="center">
                  You've not selected an announcement
                </Typography>
                <Typography variant="subtitle1" textAlign="center">
                  {isSuperAdmin ? (
                    <>
                      Click on an announcement to read it, or{' '}
                      <Typography
                        sx={{ cursor: 'pointer' }}
                        display="inline"
                        fontWeight="bold"
                        color="primary"
                        variant="subtitle1"
                        onClick={openCreate}
                      >
                        add new
                      </Typography>{' '}
                      now
                    </>
                  ) : (
                    'Click on an announcement to read it'
                  )}
                </Typography>
              </Box>
            </Box>
          )}
        </SimpleBar>
      </MainCard>

      {isSuperAdmin && (
        <AnnouncementFormDialog
          open={formOpen}
          announcement={editing}
          onClose={() => {
            setFormOpen(false);
            setEditing(null);
          }}
          onSaved={handleSaved}
        />
      )}

      <Dialog open={Boolean(pendingDelete)} onClose={deleting ? undefined : () => setPendingDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete announcement</DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{pendingDelete?.title}" will stop being visible to clients. This can be reversed only from the database.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setPendingDelete(null)} color="secondary" disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AnnouncementsPage;
